import React, { useState, useEffect } from 'react';
import { FiExternalLink, FiLoader } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import BookingStatusBadge from './BookingStatusBadge';
import { toggleBookingStatus } from '../services/roundService';

/**
 * BookingStatusSection - booking-status switch + course contact CTAs.
 *
 * Owners (canToggle=true) see a switch row to flip the round's booked state.
 * Non-owners see a read-only status badge. Both groups see the
 * Book-at-course / phone CTAs underneath when available.
 */
function BookingStatusSection({ round, canToggle, onBookingChange }) {
  const [isToggling, setIsToggling] = useState(false);
  const [localIsBooked, setLocalIsBooked] = useState(round?.is_booked || false);

  useEffect(() => {
    setLocalIsBooked(round?.is_booked || false);
  }, [round?.is_booked]);

  const handleToggle = async () => {
    if (!canToggle || isToggling) return;
    setIsToggling(true);
    const newStatus = !localIsBooked;
    const { error } = await toggleBookingStatus(round.id, newStatus);

    if (!error) {
      setLocalIsBooked(newStatus);
      onBookingChange?.(newStatus);
    } else {
      console.error('Failed to toggle booking status:', error);
    }
    setIsToggling(false);
  };

  const golfNowUrl = round?.golfnow_booking_url;
  const courseName = round?.course;

  if (!courseName || courseName === 'To be determined') {
    return null;
  }

  const showGolfNow = golfNowUrl && !localIsBooked;

  return (
    <div className="bg-surface rounded-lg p-4 border border-border-subtle space-y-3 md:space-y-0 md:flex md:items-center md:justify-between md:gap-4">
      {/* Owner: switch toggle. Non-owner: read-only badge. */}
      {canToggle ? (
        <div className="flex items-center justify-between md:justify-start md:flex-1 md:gap-3">
          <p className="text-sm font-semibold text-text-primary md:order-1">
            {localIsBooked ? 'Booked with course' : 'Mark as booked'}
          </p>
          <button
            type="button"
            onClick={handleToggle}
            disabled={isToggling}
            aria-label={localIsBooked ? 'Mark as not booked' : 'Mark as booked'}
            aria-pressed={localIsBooked}
            className={`relative w-14 h-8 rounded-full transition-colors duration-200 disabled:opacity-50 flex-shrink-0 md:order-2 ${
              localIsBooked ? 'bg-accent' : 'bg-subtle'
            }`}
          >
            <span
              className={`absolute w-6 h-6 top-1 left-1 bg-card rounded-full shadow transition-transform duration-200 flex items-center justify-center ${
                localIsBooked ? 'translate-x-6' : 'translate-x-0'
              }`}
            >
              {isToggling && (
                <SafeIcon icon={FiLoader} className="w-3 h-3 text-text-muted animate-spin" />
              )}
            </span>
          </button>
        </div>
      ) : (
        <BookingStatusBadge isBooked={localIsBooked} />
      )}

      {showGolfNow && (
        <a
          href={golfNowUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-accent text-white hover:bg-interactive-hover transition-colors font-medium text-sm flex-shrink-0"
        >
          Book on GolfNow
          <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
        </a>
      )}
    </div>
  );
}

export default BookingStatusSection;
